'use client';

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Appointment } from './types';

interface BookingsMonthViewProps {
  appointments: Appointment[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onGoToAgenda: () => void;
}

export const BookingsMonthView: React.FC<BookingsMonthViewProps> = ({
  appointments,
  selectedDate,
  onSelectDate,
  onGoToAgenda,
}) => {
  const [currentYear] = useState(2026);
  const [currentMonthIndex] = useState(9); // 9 es Octubre (0-indexed)

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const daysHeader = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  // Generar días del mes de Octubre 2026 (1 de octubre fue Jueves -> 3 días previos de Septiembre)
  // Total 31 días en Octubre
  const daysInMonth = 31;
  const firstDayWeekIndex = 3; // Jueves (0=Lunes, 1=Martes, 2=Miércoles, 3=Jueves)

  const calendarDays = [];

  // Días previos de relleno (Septiembre)
  for (let p = 28; p <= 30; p++) {
    calendarDays.push({
      dayNum: p,
      isCurrentMonth: false,
      dateStr: `2026-09-${p}`,
      isWeekend: false,
    });
  }

  // Días de Octubre
  for (let d = 1; d <= daysInMonth; d++) {
    const dayStr = d.toString().padStart(2, '0');
    const dateStr = `2026-10-${dayStr}`;
    const dayOfWeek = (firstDayWeekIndex + d - 1) % 7; // 5 es Sábado, 6 es Domingo
    const isWeekend = dayOfWeek === 5 || dayOfWeek === 6;

    calendarDays.push({
      dayNum: d,
      isCurrentMonth: true,
      dateStr,
      isWeekend,
    });
  }

  // Relleno posterior (Noviembre)
  let postDayCounter = 1;
  while (calendarDays.length % 7 !== 0) {
    calendarDays.push({
      dayNum: postDayCounter,
      isCurrentMonth: false,
      dateStr: `2026-11-0${postDayCounter}`,
      isWeekend: false,
    });
    postDayCounter++;
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs mx-4 sm:mx-6 overflow-hidden mb-12">
      {/* Cabecera del Mes */}
      <div className="p-4 border-b border-slate-200/90 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-extrabold text-[#0f172a]">
            {monthNames[currentMonthIndex]} {currentYear}
          </h2>
        </div>

        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
          <button
            title="Mes anterior"
            className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-slate-900 rounded-lg transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-bold px-2 text-slate-700">Octubre</span>
          <button
            title="Mes siguiente"
            className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-slate-900 rounded-lg transition"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Días de la semana L-D */}
      <div className="grid grid-cols-7 border-b border-slate-200 text-center text-[11px] font-black uppercase text-slate-400 py-2.5 bg-slate-50/40">
        {daysHeader.map((dh, idx) => (
          <div key={idx}>{dh}</div>
        ))}
      </div>

      {/* Cuadrícula de Celdas (Heatmap AppGenda Fase 3) */}
      <div className="grid grid-cols-7 divide-x divide-y divide-slate-100 bg-slate-50/20">
        {calendarDays.map((cd, idx) => {
          const isSelected = cd.dateStr === selectedDate;
          const isToday = cd.dateStr === '2026-10-13';

          // Turnos activos para esta fecha
          const dayAppointments = appointments.filter(
            (a) => a.dateStr === cd.dateStr && a.status.toLowerCase() !== 'cancelled'
          );
          const hasBookings = dayAppointments.length > 0;

          return (
            <div
              key={idx}
              onClick={() => {
                if (cd.isCurrentMonth) {
                  onSelectDate(cd.dateStr);
                }
              }}
              className={`min-h-[90px] sm:min-h-[110px] p-2 transition cursor-pointer flex flex-col justify-between ${
                !cd.isCurrentMonth
                  ? 'bg-slate-50/40 text-slate-300 pointer-events-none'
                  : cd.isWeekend
                  ? 'bg-slate-100/30'
                  : 'bg-white hover:bg-slate-50/80'
              } ${isToday ? 'ring-2 ring-inset ring-[#10b981]' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-xs font-black ${
                    !cd.isCurrentMonth
                      ? 'text-slate-300'
                      : isSelected
                      ? 'text-[#047857]'
                      : 'text-slate-700'
                  }`}
                >
                  {cd.dayNum}
                </span>

                {isToday && (
                  <span className="text-[9px] font-extrabold bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded">
                    Hoy
                  </span>
                )}
              </div>

              {/* Pastilla de Calor Esmeralda si hay turnos (como el día 16 en AppGenda) */}
              {hasBookings && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectDate(cd.dateStr);
                    onGoToAgenda();
                  }}
                  className="bg-[#10b981] hover:bg-[#059669] text-white rounded-xl p-2 shadow-xs transition transform hover:scale-[1.02] flex items-center justify-between mt-2"
                >
                  <span className="text-xs font-extrabold leading-none">
                    {dayAppointments.length}{' '}
                    {dayAppointments.length === 1 ? 'turno' : 'turnos'}
                  </span>
                  <span className="text-[10px] opacity-80 font-bold">Ver →</span>
                </div>
              )}

              {!hasBookings && cd.isCurrentMonth && (
                <div className="text-center text-slate-300 text-xs my-auto">—</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
